/**
 * ChatOps Parser - Phase B Mission B3
 *
 * Minimal slash command grammar for controlling Studio UI.
 * No LLM parsing yet - just simple string matching.
 *
 * Supported commands:
 * - /open convops    → set_center_app convops
 * - /open missions   → set_center_app missions
 * - /open deployment → set_center_app deployment
 * - /open builder    → set_center_app builder
 * - /open meta       → set_center_app meta (E7 Meta Telemetry Panel)
 * - /drawer          → toggle_drawer
 * - /drawer toggle   → toggle_drawer
 */

import type { StudioUICommand } from './uiCommands.js';

/**
 * App key aliases for more natural commands
 */
const APP_KEY_ALIASES: Record<string, string> = {
    convops: 'convops',
    conv: 'convops',
    conversion: 'convops',
    missions: 'missions',
    mission: 'missions',
    deployment: 'deployment',
    deploy: 'deployment',
    builder: 'builder',
    build: 'builder',
    matrix: 'matrix',
    meta: 'meta',
    telemetry: 'meta',
    os: 'meta',
};

/**
 * Parse a chat command into a StudioUICommand
 *
 * @param text - The raw input text from the chat
 * @returns A StudioUICommand if the text is a valid command, null otherwise
 */
export function parseChatCommand(text: string): StudioUICommand | null {
    const trimmed = text.trim().toLowerCase();

    // Must start with /
    if (!trimmed.startsWith('/')) {
        return null;
    }

    // Split into command and arguments
    const parts = trimmed.split(/\s+/);
    const cmd = parts[0];
    const arg = parts[1];

    // /open <app>
    if (cmd === '/open' && arg) {
        const appKey = APP_KEY_ALIASES[arg];
        if (appKey) {
            return { type: 'set_center_app', appKey };
        }
        // Unknown app, but still a valid /open command - try the raw arg
        return { type: 'set_center_app', appKey: arg };
    }

    // /drawer [toggle]
    if (cmd === '/drawer') {
        return { type: 'toggle_drawer' };
    }

    // Not a recognized UI command
    return null;
}

/**
 * Check if a text string is a UI command (without parsing it)
 */
export function isUICommand(text: string): boolean {
    const trimmed = text.trim().toLowerCase();
    return trimmed.startsWith('/open ') || trimmed.startsWith('/drawer');
}

/**
 * Get help text for available UI commands
 */
export function getUICommandHelp(): string {
    return `Available UI commands:
  /open convops    - Open ConvOps console
  /open missions   - Open Missions panel
  /open deployment - Open Deployment panel
  /open builder    - Open Builder panel
  /open meta       - Open Meta Telemetry panel
  /drawer          - Toggle the bottom drawer`;
}

export default parseChatCommand;
